<script setup lang="ts">
// WorkspaceSwitcher (contract 07 §10, ORP-18636) — replaces v3 `components/App/WorkspaceSelect.vue`
// (100 ln). v3 reached `workspaceStore`/`accountStore`/`layoutStore` + `router` directly (`:45-48`),
// raised a `swalAlert` (`:65`) with a hardcoded upgrade string (`:67`), gated on a raw permission
// magic-string (`:6`, `:61`) and called `layoutStore.activeOnboard()` (`:63`). NONE of that is here:
// ZERO store / router / swal / layoutStore reach. It reads `useWorkspace()`, drives the switch via
// `useWorkspace().selectWorkspace` (→ `SelectWorkspaceFlow`) and emits for its host (05).
//
// Selecting a workspace drives the `<RouterView :key="workspaceId">` remount (05 §7) — the shell
// stays mounted and `session.status` stays `ready`, so there is no splash: the control simply
// disables while the switch runs.
//
// SCOPE (contract §10): the v3 "New workspace" affordance + its upgrade-plan alert are NOT ported
// here. §10 gives this component `select(ws)` and the four states only; the package-limit signal is
// `PackageService`'s and the modal it raises is a credits/account UI concern.
import { computed, ref } from 'vue'

import { useAccount } from '../composables/useAccount'
import { useAccountSession } from '../composables/useAccountSession'
import { useWorkspace } from '../composables/useWorkspace'
import type { WorkspaceItem } from '../models/workspace.types'

import { useI18n } from '@/shared/i18n'
import { Button } from '@/shared/ui/button'
import { useToast } from '@/shared/ui/feedback'
import { Icon } from '@/shared/ui/icon'
import { Select, type SelectOption } from '@/shared/ui/select'
import { Skeleton } from '@/shared/ui/skeleton'
import type { UiAsyncState } from '@/shared/ui/types'

const props = withDefaults(defineProps<{ variant?: 'select' | 'menu'; disabled?: boolean }>(), {
  variant: 'select',
  disabled: false,
})
const emit = defineEmits<{
  select: [ws: WorkspaceItem]
  onboard: []
  settings: [ws: WorkspaceItem]
  switching: [value: boolean]
}>()

const { t } = useI18n()
const toast = useToast()
const { accountId } = useAccount()
const { workspaces, workspaceId, selectWorkspace } = useWorkspace()
const { isBooting } = useAccountSession()

/** Local pending state — a switch is not a re-boot, so the shell keeps rendering and only this
 *  control disables (contract §7/§18). */
const switching = ref(false)
const visibleWorkspaces = computed(() => {
  if (accountId.value === undefined) return workspaces.value
  return workspaces.value.filter(
    (ws) => ws.account_id === undefined || String(ws.account_id) === String(accountId.value),
  )
})

/** loading → skeleton · empty → "no workspaces" + onboarding CTA · ready → the Select.
 *  `switching` is not a `UiAsyncState`; it disables the control instead (contract §10). */
const state = computed<UiAsyncState>(() => {
  if (isBooting.value) return 'loading'
  return visibleWorkspaces.value.length === 0 ? 'empty' : 'ready'
})

// Ids are `string | number` on the wire; the option value is normalised to a string so the primitive
// has one value type and the reverse lookup can never mismatch on 5 vs '5'.
const options = computed<SelectOption<string>[]>(() =>
  visibleWorkspaces.value.map((ws) => ({
    value: String(ws.id),
    label: String(ws.label || ws.name || ws.id),
  })),
)

const selectedId = computed<string | null>(() => {
  if (workspaceId.value === undefined) return null
  return visibleWorkspaces.value.some((ws) => String(ws.id) === String(workspaceId.value))
    ? String(workspaceId.value)
    : null
})

async function onChange(value: string | null): Promise<void> {
  const ws = visibleWorkspaces.value.find((item) => String(item.id) === value)
  if (!ws) return
  switching.value = true
  emit('switching', true)
  emit('select', ws)
  try {
    const outcome = await selectWorkspace(ws)
    // A target the user cannot access mutated nothing; a same-id re-select finalised only. Copy is
    // resolved HERE — the flow classifies by `ApiError` and never resolves an i18n key itself.
    if (outcome.status === 'noAccess') toast.warning(t('account.switch.noAccess'))
    if (outcome.status === 'failed') toast.error(t('account.switch.failed'))
  } finally {
    switching.value = false
    emit('switching', false)
  }
}

function isCurrent(ws: WorkspaceItem): boolean {
  return String(ws.id) === selectedId.value
}

function timezone(ws: WorkspaceItem): string {
  return typeof ws.timezone === 'string' ? ws.timezone : ''
}

function chooseFromMenu(ws: WorkspaceItem): void {
  void onChange(String(ws.id))
}
</script>

<template>
  <Skeleton v-if="state === 'loading'" class="workspace-switcher__skeleton" />
  <div v-else-if="state === 'empty'" class="workspace-switcher__empty grid gap-sm p-sm">
    <p>{{ t('account.workspaceSwitcher.empty') }}</p>
    <Button
      variant="primary"
      size="sm"
      :disabled="props.disabled || switching"
      @click="emit('onboard')"
    >
      {{ t('account.workspaceSwitcher.onboarding') }}
    </Button>
  </div>
  <div v-else-if="props.variant === 'menu'" class="workspace-switcher__menu grid">
    <div
      v-for="ws in visibleWorkspaces"
      :key="ws.id"
      class="workspace-switcher__row flex min-h-13 items-center rounded-sm [&:hover]:bg-surface-sunken [&:focus-within]:bg-surface-sunken"
    >
      <button
        type="button"
        class="workspace-switcher__option flex w-full min-h-13 items-center gap-xs py-xs px-sm border-0 rounded-sm bg-transparent text-fg text-start cursor-pointer [&:focus-visible]:outline-none [&:disabled]:opacity-55 [&:disabled]:cursor-not-allowed"
        :disabled="props.disabled || switching"
        @click="chooseFromMenu(ws)"
      >
        <span
          class="workspace-switcher__radio w-4 h-4 flex-none border border-border-strong rounded-full"
          :class="{ 'workspace-switcher__radio--selected': isCurrent(ws) }"
          aria-hidden="true"
        />
        <span class="workspace-switcher__copy min-w-0 flex-1">
          <strong>{{ ws.label || ws.name }}</strong>
          <small v-if="timezone(ws)">{{ timezone(ws) }}</small>
        </span>
      </button>
      <button
        type="button"
        class="workspace-switcher__settings grid w-8 h-8 flex-none place-items-center p-0 border-0 bg-transparent text-fg cursor-pointer [&:focus-visible]:rounded-sm [&:focus-visible]:outline-[length:2px] [&:focus-visible]:outline-solid [&:focus-visible]:outline-info [&:focus-visible]:outline-offset-[1px] [&:disabled]:opacity-55 [&:disabled]:cursor-not-allowed"
        :aria-label="t('account.workspaceSwitcher.settings', { workspace: ws.label || ws.name })"
        :disabled="props.disabled || switching"
        @click="emit('settings', ws)"
      >
        <Icon name="gear" :size="14" aria-hidden="true" />
      </button>
    </div>
  </div>
  <Select
    v-else
    class="workspace-switcher"
    :model-value="selectedId"
    :options="options"
    :label="t('account.workspaceSwitcher.label')"
    :placeholder="t('account.workspaceSwitcher.placeholder')"
    :disabled="props.disabled || switching"
    searchable
    @change="onChange"
  />
</template>

<style scoped>
.workspace-switcher__skeleton .workspace-switcher__empty p {
  margin: 0;
  color: var(--ui-color-muted);
  font-size: 0.8125rem;
}
.workspace-switcher__skeleton .workspace-switcher__radio--selected {
  border: 4px solid var(--ui-color-info);
}
.workspace-switcher__skeleton .workspace-switcher__copy > *,
.workspace-switcher__empty .workspace-switcher__copy > *,
.workspace-switcher__menu .workspace-switcher__copy > *,
.workspace-switcher .workspace-switcher__copy > * {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.workspace-switcher__skeleton .workspace-switcher__copy strong {
  font-size: 0.875rem;
  line-height: 1.25;
}
.workspace-switcher__skeleton .workspace-switcher__copy small {
  margin-top: 0.125rem;
  color: var(--ui-color-muted);
  font-size: 0.75rem;
  line-height: 1.25;
}
.workspace-switcher__empty p {
  margin: 0;
  color: var(--ui-color-muted);
  font-size: 0.8125rem;
}
.workspace-switcher__empty .workspace-switcher__radio--selected {
  border: 4px solid var(--ui-color-info);
}
.workspace-switcher__empty .workspace-switcher__copy strong {
  font-size: 0.875rem;
  line-height: 1.25;
}
.workspace-switcher__empty .workspace-switcher__copy small {
  margin-top: 0.125rem;
  color: var(--ui-color-muted);
  font-size: 0.75rem;
  line-height: 1.25;
}
.workspace-switcher__menu .workspace-switcher__empty p {
  margin: 0;
  color: var(--ui-color-muted);
  font-size: 0.8125rem;
}
.workspace-switcher__menu .workspace-switcher__radio--selected {
  border: 4px solid var(--ui-color-info);
}
.workspace-switcher__menu .workspace-switcher__copy strong {
  font-size: 0.875rem;
  line-height: 1.25;
}
.workspace-switcher__menu .workspace-switcher__copy small {
  margin-top: 0.125rem;
  color: var(--ui-color-muted);
  font-size: 0.75rem;
  line-height: 1.25;
}
.workspace-switcher .workspace-switcher__empty p {
  margin: 0;
  color: var(--ui-color-muted);
  font-size: 0.8125rem;
}
.workspace-switcher .workspace-switcher__radio--selected {
  border: 4px solid var(--ui-color-info);
}
.workspace-switcher .workspace-switcher__copy strong {
  font-size: 0.875rem;
  line-height: 1.25;
}
.workspace-switcher .workspace-switcher__copy small {
  margin-top: 0.125rem;
  color: var(--ui-color-muted);
  font-size: 0.75rem;
  line-height: 1.25;
}
</style>
